import React, { useState, useEffect } from 'react';
import { ERPModule, User, AppNotification, Country } from './types';
import Sidebar from './components/Sidebar';
import Login from './components/Login';
import { apiService } from './services/apiService';
import DashboardModule from './modules/DashboardModule';
import InfrastructureModule from './modules/Projects'; 
import LogisticsModule from './modules/LogisticsModule';
import PortBitumenTrackerModule from './modules/PortBitumenTrackerModule';
import CRMModule from './modules/CRMModule';
import FinanceModule from './modules/FinanceModule';
import TaskModule from './modules/TaskModule';
import UserModule from './modules/UserModule';
import { Menu, LogOut, Lock, Globe, ChevronDown, ShieldAlert } from 'lucide-react';

const AccessDenied: React.FC<{ title: string; message: string; onReturn: () => void }> = ({ title, message, onReturn }) => (
  <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-slate-50 animate-in fade-in duration-500">
    <div className="w-24 h-24 bg-rose-50 text-rose-500 rounded-[40px] flex items-center justify-center mb-8 border border-rose-100 shadow-xl shadow-rose-500/5">
      <ShieldAlert size={48} />
    </div>
    <h2 className="text-3xl font-black text-slate-800 uppercase tracking-tight">{title}</h2>
    <p className="text-slate-500 mt-4 max-w-sm font-medium leading-relaxed">{message}</p>
    <button 
      onClick={onReturn}
      className="mt-10 px-10 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-all shadow-2xl active:scale-95 flex items-center gap-3"
    >
      <Lock size={16} /> Return to Portal
    </button>
  </div>
);

const App: React.FC = () => {
  const [activeModule, setActiveModule] = useState<ERPModule>(ERPModule.DASHBOARD);
  const [activeCountry, setActiveCountry] = useState<Country>(Country.TANZANIA);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);

  useEffect(() => {
    const checkSession = () => {
      const savedUser = localStorage.getItem('shiv_erp_session');
      if (savedUser) {
        try {
          const user = JSON.parse(savedUser);
          if (user && user.id) {
              if (user.status === 'disabled') {
                  localStorage.removeItem('shiv_erp_session');
              } else {
                  setCurrentUser(user);
                  if (user.homeCountry) setActiveCountry(user.homeCountry);
              }
          }
        } catch (e) {
          localStorage.removeItem('shiv_erp_session');
        }
      }
    };
    checkSession();

    const savedNotifs = localStorage.getItem('nexus_notifications');
    if (savedNotifs) {
      try {
        setNotifications(JSON.parse(savedNotifs));
      } catch (e) {
        console.error("Failed to load notifications", e);
      }
    }
    
    const timer = setTimeout(() => setIsInitializing(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const handleLogin = async (username: string, password: string): Promise<boolean> => {
    try {
        const user = await apiService.login(username, password);
        
        if (user) {
          if (user.status === 'disabled') {
              alert("Access Denied: Your account is suspended. Contact system administrator.");
              return false;
          }

          const isAdmin = user.role.toLowerCase() === 'admin';
          const sessionUser = {
            ...user,
            allowedModules: (isAdmin || !user.allowedModules || user.allowedModules.length === 0) 
                ? (isAdmin ? Object.values(ERPModule) : [ERPModule.DASHBOARD]) 
                : user.allowedModules
          };
          
          localStorage.setItem('shiv_erp_session', JSON.stringify(sessionUser));
          setCurrentUser(sessionUser);
          if (sessionUser.homeCountry) setActiveCountry(sessionUser.homeCountry);
          return true;
        }
        return false;
    } catch (err) {
        console.error("Auth Exception:", err);
        return false;
    }
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem('shiv_erp_session');
  };

  const addNotification = (notif: AppNotification) => {
    setNotifications(prev => {
      const isDuplicate = prev.some(n => n.id === notif.id);
      if (isDuplicate) return prev;
      const next = [notif, ...prev];
      localStorage.setItem('nexus_notifications', JSON.stringify(next));
      return next;
    });
  };

  const getCountryColor = (c: Country) => {
    switch(c) {
      case Country.TANZANIA: return 'from-emerald-500 to-indigo-600';
      case Country.UGANDA: return 'from-yellow-400 to-red-600';
      case Country.KENYA: return 'from-red-600 to-slate-900';
      default: return 'from-slate-600 to-slate-800';
    }
  };

  const renderModule = () => {
    if (!currentUser) return null;

    const isAdmin = currentUser.role.toLowerCase() === 'admin';
    const isModuleAuthorized = isAdmin || (currentUser.allowedModules && currentUser.allowedModules.includes(activeModule));
    
    if (activeModule === ERPModule.USERS && !isAdmin) {
        return (
            <AccessDenied 
                title="Root Access Required" 
                message="The Identity and Access Management module requires technical administrator clearance levels." 
                onReturn={() => setActiveModule(ERPModule.DASHBOARD)} 
            />
        );
    }

    if (!isModuleAuthorized) {
        return (
            <AccessDenied 
                title="Module Restricted" 
                message={`Your current clearance profile (${currentUser.role.toUpperCase()}) does not grant visibility into the ${activeModule.replace('_', ' ')} suite.`} 
                onReturn={() => setActiveModule(currentUser.allowedModules?.[0] || ERPModule.DASHBOARD)} 
            />
        );
    }

    switch (activeModule) {
      case ERPModule.DASHBOARD: return <DashboardModule activeCountry={activeCountry} />;
      case ERPModule.INFRASTRUCTURE: return <InfrastructureModule currentUser={currentUser} activeCountry={activeCountry} />;
      case ERPModule.ORDERS_PIPELINE: return <LogisticsModule currentUser={currentUser} activeCountry={activeCountry} />;
      case ERPModule.PORT_BIT_TRACKER: return <PortBitumenTrackerModule currentUser={currentUser} activeCountry={activeCountry} />;
      case ERPModule.CRM: return <CRMModule currentUser={currentUser} activeCountry={activeCountry} />;
      case ERPModule.QUOTES: return <FinanceModule currentUser={currentUser} activeCountry={activeCountry} />;
      case ERPModule.TASKS: return <TaskModule currentUser={currentUser} onAddNotification={addNotification} activeCountry={activeCountry} />;
      case ERPModule.USERS: return <UserModule currentUser={currentUser} />;
      default: return <DashboardModule activeCountry={activeCountry} />;
    }
  };

  if (isInitializing) {
      return (
          <div className="h-screen w-screen flex items-center justify-center bg-slate-900 text-white">
              <div className="flex flex-col items-center gap-4">
                  <div className="h-12 w-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[10px] font-black uppercase tracking-[0.3em]">Establishing Nexus Core Link</p>
              </div>
          </div>
      );
  }

  if (!currentUser) {
    return <Login onLogin={handleLogin} />;
  }

  const isBranchSwitchHidden = activeModule === ERPModule.QUOTES;
  const canSwitchBranch = currentUser.role.toLowerCase() === 'admin';

  return (
    <div className="flex h-screen bg-slate-50 text-slate-900 overflow-hidden font-sans">
      <div className={`transition-all duration-300 ease-in-out overflow-hidden flex shrink-0 ${isSidebarOpen ? (isSidebarCollapsed ? 'w-20' : 'w-64') : 'w-0'}`}>
        <Sidebar 
          activeModule={activeModule} 
          activeCountry={activeCountry}
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          onModuleChange={(m) => setActiveModule(m)} 
          onCountryChange={(c) => setActiveCountry(c)}
          user={currentUser}
          onClose={() => setIsSidebarOpen(false)}
        />
      </div>
      
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-8 shrink-0 z-30">
          <div className="flex items-center gap-4">
            {!isSidebarOpen && (
              <button 
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 -ml-2 text-slate-500 hover:text-indigo-600 hover:bg-slate-50 rounded-xl transition-all"
                title="Show Sidebar"
              >
                <Menu size={20} />
              </button>
            )}
            <div className="flex items-center gap-3">
              <div className={`px-3 py-1 bg-gradient-to-r ${getCountryColor(activeCountry)} rounded-lg shadow-sm`}>
                <span className="text-[10px] font-black text-white uppercase tracking-widest">{activeCountry} Branch</span>
              </div>
              <span className="text-slate-300">/</span>
              <h1 className="text-sm font-bold text-slate-700 capitalize">
                {activeModule.toLowerCase().replace('_', ' ')}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-6">
            {canSwitchBranch && !isBranchSwitchHidden && (
              <div className="relative group">
                <button className="flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-slate-100 rounded-xl transition-all border border-slate-200">
                  <Globe size={16} className="text-indigo-600" />
                  <span className="text-xs font-black uppercase tracking-tight text-slate-700">Switch Branch</span>
                  <ChevronDown size={14} className="text-slate-400 group-hover:rotate-180 transition-transform" />
                </button>
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-2xl border border-slate-100 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  {(Object.values(Country) as Country[]).map(c => (
                    <button 
                      key={c}
                      onClick={() => setActiveCountry(c)}
                      className={`w-full text-left px-5 py-3 text-xs font-bold uppercase tracking-tight hover:bg-slate-50 flex items-center gap-3 ${activeCountry === c ? 'text-indigo-600' : 'text-slate-500'}`}
                    >
                      <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${getCountryColor(c)}`}></div>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 border-l border-slate-100 pl-6">
                <div className="hidden md:flex flex-col items-end mr-2 text-right">
                    <span className="text-xs font-black text-slate-800 tracking-tight">{currentUser.fullName}</span>
                    <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest leading-none mt-0.5">{currentUser.role} Control</span>
                </div>
                <button 
                    onClick={handleLogout}
                    className="p-2 text-slate-400 hover:text-rose-600 transition-colors hover:bg-rose-50 rounded-xl"
                    title="Logout"
                >
                <LogOut size={20} />
                </button>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto custom-scrollbar bg-slate-50/50">
          {renderModule()}
        </div>
      </main>
    </div>
  );
};

export default App;